import { AlertTriangle, Clock3, Radio } from 'lucide-react';
import {
  firstAvailableSeries,
  useAllocatorAccessStatus,
  useAllocatorSeries,
  useAllocatorWorkspaceV1,
} from '@/lib/allocator-v1';

function compactTime(value: string | null | undefined) {
  if (!value) return 'pending';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'reported';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(date);
}

export function EvidenceFreshnessBar() {
  const access = useAllocatorAccessStatus();
  const workspace = useAllocatorWorkspaceV1();
  const series = useAllocatorSeries();
  const formal = firstAvailableSeries(series.data?.items);
  const funded = workspace.data?.allocations.some((allocation) => allocation.status === 'funded');
  const active = access.data?.state === 'active';

  return (
    <div
      aria-label="Allocator evidence freshness"
      aria-live="polite"
      className="qf-freshness-bar border-t px-4 py-2 sm:px-6 lg:px-8"
      role="status"
    >
      <div className="flex min-w-0 items-center justify-between gap-4 text-xs">
        <div
          className={`flex min-w-0 items-center gap-2 ${active ? 'text-[#7AD8CC]' : 'text-amber-200'}`}
        >
          <AlertTriangle aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate font-semibold">
            {active ? 'Authorized workspace' : 'No active grant'} ·{' '}
            {funded ? 'funding recorded' : 'no funding recorded'}
          </span>
        </div>

        <div className="hidden min-w-0 items-center gap-5 text-slate-400 sm:flex">
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <Clock3 aria-hidden="true" className="h-3.5 w-3.5" />
            Projection {compactTime(formal?.publishedAt)} UTC
          </span>
          <span className="hidden whitespace-nowrap lg:inline">
            Authority {compactTime(access.data?.checkedAt)} UTC
          </span>
          <span
            aria-label={
              formal ? 'Formal live-capital evidence available' : 'Formal evidence unavailable'
            }
            className={
              formal
                ? 'flex items-center gap-1.5 text-emerald-300'
                : 'flex items-center gap-1.5 text-amber-200'
            }
          >
            <Radio aria-hidden="true" className="h-3.5 w-3.5" />
            {formal ? 'Formal evidence' : 'Evidence pending'}
          </span>
        </div>
      </div>
    </div>
  );
}
